import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType, TableDynamicColumn } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  createRuntimeFieldValueMap,
  type RuntimeFieldValueMap,
} from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import {
  createEmptyObjectPassportElement,
  mergeLoadedObjectPassportElement,
  type ObjectPassportElement,
} from '@/features/Inventory/ObjectListEdit/model/model'
import {
  isElementLinkedFieldCell,
  isLinkedReferenceGridLayoutItem,
  resolvePassportFieldPaletteSource,
} from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import {
  parseHopSegmentsForLinkedResolution,
  parseLeafFieldIdForLinkedResolution,
  parseLeafFromElementId,
  parseLinkFieldIdFromElementId,
  parseRefHopChainFromGridId,
} from './passportRefGridLinkedParse'
import {
  resolveRefDictionaryDisplayChain,
  type ObjectTypeDictionaryPayload,
} from './refDictionaryLinkedResolve'
import { isParentObjectNameGridId } from './parentObjectNameGrid'
import {
  isListCatalogNameGridId,
  parseListCatalogNameTypeId,
} from './listCatalogNameGrid'
import { formatLocalWallClockVerDateNow } from '@/shared/lib'

/** Shared across `buildLinkedPassportDisplayMapForRuntime` runs so dictionary GETs are not repeated when the watcher re-runs. */
const dictCacheSingleton = new Map<string, ObjectTypeDictionaryPayload>()
const dictInflightSingleton = new Map<
  string,
  Promise<ObjectTypeDictionaryPayload | null>
>()

function passportLinkedResolveCacheKey(
  groupId: string,
  listItemId: number,
  statusDate: string | null | undefined,
): string {
  const ver = statusDate == null || statusDate === '' ? '' : String(statusDate)
  return `${groupId}:${listItemId}:${ver}`
}

/** Same as dictionary: linked passport row GETs repeat every watcher pass if maps are per-call. */
const passportCacheSingleton = new Map<string, ObjectPassportElement>()
const passportInflightSingleton = new Map<string, Promise<ObjectPassportElement | null>>()

const inventCatalogTypeLabelCache = new Map<string, string>()
const inventCatalogTypeLabelInflight = new Map<string, Promise<string>>()

function unwrapObjectTypeGetBody(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const inner = r.data
  if (inner != null && typeof inner === 'object') return inner as Record<string, unknown>
  return r
}

function pickNonEmptyStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
  }
  return ''
}

function catalogLabelFromObjectTypePayload(raw: unknown): string {
  const body = unwrapObjectTypeGetBody(raw)
  if (!body) return ''
  const iot = body.invent_object_type
  if (iot && typeof iot === 'object') {
    const o = iot as Record<string, unknown>
    const fromIot = pickNonEmptyStr(o.name, o.object_name, o.title)
    if (fromIot) return fromIot
  }
  return pickNonEmptyStr(body.name, body.object_name, body.type_name, body.object_type_name)
}

async function fetchInventCatalogTypeDisplayLabel(
  http: HttpGet,
  groupId: string,
  inventTypeId: number,
): Promise<string> {
  if (!groupId || !Number.isFinite(inventTypeId) || inventTypeId <= 0) return ''
  const key = `${groupId}:${inventTypeId}`
  const hit = inventCatalogTypeLabelCache.get(key)
  if (hit !== undefined) return hit
  const pending = inventCatalogTypeLabelInflight.get(key)
  if (pending) return pending
  const p = (async () => {
    try {
      const res = await http.get(`/inventory/group/${groupId}/object-types/${inventTypeId}`)
      const label = catalogLabelFromObjectTypePayload(res.data)
      inventCatalogTypeLabelCache.set(key, label)
      return label
    } catch {
      inventCatalogTypeLabelCache.set(key, '')
      return ''
    }
  })()
  inventCatalogTypeLabelInflight.set(key, p)
  try {
    return await p
  } finally {
    inventCatalogTypeLabelInflight.delete(key)
  }
}

export type { LinkedHopSegment } from './passportRefGridLinkedParse'
export {
  findFirstFieldDefaultValueById,
  parseHopSegmentsForLinkedResolution,
  parseLeafFieldIdForLinkedResolution,
  parseLeafFromElementId,
  parseLinkFieldIdFromElementId,
  parseRefHopChainFromGridId,
} from './passportRefGridLinkedParse'
export {
  normalizeObjectTypeDictionaryPayload,
  resolveRefDictionaryDisplayChain,
} from './refDictionaryLinkedResolve'

type HttpGet = {
  get: (url: string) => Promise<{ data?: unknown }>
}

type RuntimeFilter = {
  status_date: null | string
}

function toRuntimeFieldArray(
  value: ObjectPassportElement['fields'],
): Array<{ id: string | number; value?: unknown }> {
  if (Array.isArray(value)) return value
  return Object.entries(value ?? {}).map(([id, fieldValue]) => ({ id, value: fieldValue }))
}

/** Axios response → first passport element (same shape as `useLoadListItem`). */
function firstPassportElementFromGet(
  res: { data?: unknown },
): Partial<ObjectPassportElement> | null {
  const d = res.data
  if (Array.isArray(d)) return (d[0] as Partial<ObjectPassportElement>) ?? null
  if (d && typeof d === 'object' && 'data' in d && Array.isArray((d as { data: unknown[] }).data)) {
    return ((d as { data: unknown[] }).data[0] as Partial<ObjectPassportElement>) ?? null
  }
  return null
}

function parseLinkedObjectId(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw > 0 ? raw : null
  const s = String(raw).trim()
  if (!s) return null
  const n = Number(s)
  if (Number.isFinite(n) && n > 0) return n
  return null
}

function fieldMapForElement(
  element: ObjectPassportElement,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): RuntimeFieldValueMap {
  return createRuntimeFieldValueMap(
    fieldMetadata ?? element.invent_object_type?.fields,
    toRuntimeFieldArray(element.fields),
  )
}

function resolveRawFieldDisplay(
  raw: unknown,
  fieldMeta: ObjectInventoryField | undefined,
): string {
  if (raw === undefined || raw === null) return ''
  if (fieldMeta && (fieldMeta.type === 'list' || fieldMeta.type === 'list_types')) {
    const matched = fieldMeta.items?.find((it) => String(it.id) === String(raw))
    if (matched) return matched.name
  }
  return String(raw)
}

async function followLinkedSegments(
  http: HttpGet,
  filter: RuntimeFilter,
  groupId: string,
  startMap: RuntimeFieldValueMap,
  segments: LinkedHopSegment[],
): Promise<{ fieldValues: RuntimeFieldValueMap; fields: ObjectInventoryField[] } | null> {
  let currentMap = startMap
  let currentFields: ObjectInventoryField[] = []
  for (const seg of segments) {
    const edgeKey = String(seg.field_id)
    const nextId = parseLinkedObjectId(currentMap[edgeKey])
    if (nextId == null) return null
    const row = await fetchPassportElement(http, filter, groupId, nextId)
    if (!row) return null
    currentFields = row.invent_object_type?.fields ?? []
    currentMap = fieldMapForElement(row, currentFields)
  }
  return { fieldValues: currentMap, fields: currentFields }
}

async function resolveElementLinkedFieldDisplay(
  http: HttpGet,
  filter: RuntimeFilter,
  groupId: string,
  rootFieldValues: RuntimeFieldValueMap,
  item: GridFieldType,
): Promise<string> {
  const linkFieldId = item.link_field_id ?? parseLinkFieldIdFromElementId(String(item.id))
  if (linkFieldId == null) return ''
  const linkedObjectId = parseLinkedObjectId(rootFieldValues[String(linkFieldId)])
  if (linkedObjectId == null) return ''
  const row = await fetchPassportElement(http, filter, groupId, linkedObjectId)
  if (!row) return ''
  const meta = row.invent_object_type?.fields ?? []
  const linkedMap = fieldMapForElement(row, meta)
  const linkedObjectName = String(row.name ?? '').trim()
  const leafId = item.inventory_field_id ?? parseLeafFromElementId(String(item.id))
  if (leafId == null) return linkedObjectName
  const segments = item.nested_binding?.segments ?? []
  if (segments.length === 0) {
    const leafMeta = meta.find((f) => String(f.id) === String(leafId))
    return resolveRawFieldDisplay(linkedMap[String(leafId)], leafMeta) || linkedObjectName
  }
  const final = await followLinkedSegments(http, filter, groupId, linkedMap, segments)
  if (!final) return linkedObjectName
  const leafMeta = final.fields.find((f) => String(f.id) === String(leafId))
  return resolveRawFieldDisplay(final.fieldValues[String(leafId)], leafMeta) || linkedObjectName
}

async function fetchPassportElement(
  http: HttpGet,
  filter: RuntimeFilter,
  groupId: string,
  listItemId: number,
): Promise<ObjectPassportElement | null> {
  if (listItemId <= 0 || !groupId) return null
  const cache = passportCacheSingleton
  const inflight = passportInflightSingleton
  const key = passportLinkedResolveCacheKey(groupId, listItemId, filter.status_date)
  const hit = cache.get(key)
  if (hit) return hit

  const pending = inflight.get(key)
  if (pending) return pending

  const p = (async () => {
    const params = new URLSearchParams({ object_ids: String(listItemId) })
    if (filter.status_date) params.set('status_date', filter.status_date)
    if (!filter.status_date) params.set('client_now', formatLocalWallClockVerDateNow())
    const res = await http.get(`/inventory/objects/passport?${params}`)
    const raw = firstPassportElementFromGet(res)
    if (!raw) return null
    const merged = mergeLoadedObjectPassportElement(createEmptyObjectPassportElement(), raw)
    cache.set(key, merged)
    return merged
  })()

  inflight.set(key, p)
  try {
    return await p
  } finally {
    inflight.delete(key)
  }
}

async function resolveDisplayForGridItem(
  http: HttpGet,
  filter: RuntimeFilter,
  groupId: string,
  rootFieldValues: RuntimeFieldValueMap,
  item: GridFieldType,
  catalogTypeId: number,
  catalogFields: ObjectInventoryField[],
  fieldResolved: ObjectInventoryField | undefined,
  dictCache: Map<string, ObjectTypeDictionaryPayload>,
  dictInflight: Map<string, Promise<ObjectTypeDictionaryPayload | null>>,
  parentObjectDisplayName?: string,
): Promise<string> {
  const gridId = String(item.id)
  if (isParentObjectNameGridId(gridId)) {
    return String(parentObjectDisplayName ?? '').trim()
  }
  if (isListCatalogNameGridId(gridId)) {
    const tid = parseListCatalogNameTypeId(gridId)
    if (tid == null) return ''
    return (await fetchInventCatalogTypeDisplayLabel(http, groupId, tid)).trim()
  }
  const resolveViaPassportChain = async (): Promise<string> => {
    const segments = parseHopSegmentsForLinkedResolution(
      item,
      catalogTypeId,
      catalogFields,
      fieldResolved,
    )
    const leafId = parseLeafFieldIdForLinkedResolution(item, fieldResolved)
    if (leafId == null) return ''

    // LIST table columns / own grid cells: no `list_types` hops — value lives on the root row map.
    if (segments.length === 0) {
      return resolveRawFieldDisplay(rootFieldValues[String(leafId)], fieldResolved)
    }

    const final = await followLinkedSegments(http, filter, groupId, rootFieldValues, segments)
    if (!final) return ''
    const leafMeta = final.fields.find((f) => String(f.id) === String(leafId))
    return resolveRawFieldDisplay(final.fieldValues[String(leafId)], leafMeta)
  }

  if (isElementLinkedFieldCell(item)) {
    return resolveElementLinkedFieldDisplay(http, filter, groupId, rootFieldValues, item)
  }

  if (gridId.startsWith('ref:')) {
    const refSegments = parseRefHopChainFromGridId(gridId)
    if (refSegments.length > 0) {
      const leafId = parseLeafFieldIdForLinkedResolution(item, fieldResolved)
      const shouldSkipLeaf = leafId == null
      if (shouldSkipLeaf) {
        return ''
      }
      const dictValue = await resolveRefDictionaryDisplayChain(
        http,
        groupId,
        rootFieldValues,
        refSegments,
        leafId,
        dictCache,
        dictInflight,
      )
      if (dictValue !== '') return dictValue
      return resolveViaPassportChain()
    }
  }
  return resolveViaPassportChain()
}

/**
 * Resolves a LIST table column cell for one loaded child row (same nested_binding / dictionary rules as grid «Справочники»).
 */
export async function resolveRuntimeTableColumnDisplay(
  http: HttpGet,
  filter: RuntimeFilter,
  groupId: string,
  childCatalogTypeId: number,
  childFieldValues: RuntimeFieldValueMap,
  column: TableDynamicColumn,
  catalogFields: ObjectInventoryField[],
  fieldResolved: ObjectInventoryField | undefined,
): Promise<string> {
  const synthetic = {
    id: `table:${column.id}`,
    name: column.name,
    type: 'field' as const,
    row: 0,
    cell: 0,
    length: 1,
    nested_binding: column.nested_binding ?? null,
    inventory_field_id: column.field_id,
    link_field_id: column.link_field_id,
  } as GridFieldType
  return resolveDisplayForGridItem(
    http,
    filter,
    groupId,
    childFieldValues,
    synthetic,
    childCatalogTypeId,
    catalogFields,
    fieldResolved,
    dictCacheSingleton,
    dictInflightSingleton,
    undefined,
  )
}

export type CollectParentGridItemsInput = {
  layoutTabs: Array<{
    sections: Array<{
      kind: string
      items: Array<
        | {
            kind: 'field'
            layoutItem: GridFieldType
            field: ObjectInventoryField
          }
        | { kind: string; layoutItem: GridFieldType }
      >
    }>
  }>
  catalogTypeId: number
  metadataById: Map<string, ObjectInventoryField>
}

export function collectParentGridFieldItemsForLinkedResolution(
  input: CollectParentGridItemsInput,
): Array<{ layoutItem: GridFieldType; field: ObjectInventoryField | undefined }> {
  const seen = new Set<string>()
  const out: Array<{ layoutItem: GridFieldType; field: ObjectInventoryField | undefined }> = []
  for (const tab of input.layoutTabs) {
    for (const sec of tab.sections) {
      if (sec.kind !== 'row') continue
      for (const it of sec.items) {
        const layoutItem = it.layoutItem
        const field =
          'field' in it && it.kind === 'field' ? it.field : undefined
        const inv = (layoutItem as GridFieldType & { inventory_field_id?: string | number })
          .inventory_field_id
        const fieldMeta =
          field ??
          (inv != null ? input.metadataById.get(String(inv)) : undefined) ??
          input.metadataById.get(String(layoutItem.id))
        const gid = String(layoutItem.id)
        if (isParentObjectNameGridId(gid)) {
          if (seen.has(gid)) continue
          seen.add(gid)
          out.push({ layoutItem, field })
          continue
        }
        if (isListCatalogNameGridId(gid)) {
          if (seen.has(gid)) continue
          seen.add(gid)
          out.push({ layoutItem, field })
          continue
        }
        const linkedLayoutHint = isLinkedReferenceGridLayoutItem(layoutItem)
        const palette = resolvePassportFieldPaletteSource(
          layoutItem,
          input.catalogTypeId,
          fieldMeta ?? null,
        )
        if (palette !== 'parent' && !linkedLayoutHint) continue
        if (seen.has(gid)) continue
        seen.add(gid)
        out.push({ layoutItem, field })
      }
    }
  }
  return out
}

/**
 * Корневое поле, значение которого питает резолв конкретной связанной ячейки
 * (зеркалит ветвление `resolveDisplayForGridItem`). `null` — ячейка не зависит
 * от значений полей (имя родителя / имя типа каталога).
 */
function resolveLinkedSourceFieldId(
  item: GridFieldType,
  catalogTypeId: number,
  catalogFields: ObjectInventoryField[],
  fieldResolved: ObjectInventoryField | undefined,
): string | number | null {
  const gridId = String(item.id)

  // parent-obj-name / list-catalog-name — берутся из имени/типа, значения полей не читают.
  if (isParentObjectNameGridId(gridId)) return null
  if (isListCatalogNameGridId(gridId)) return null

  // element:{linkFieldId}#f:… — источник это id связанного объекта (`link_field_id`).
  if (isElementLinkedFieldCell(item)) {
    return item.link_field_id ?? parseLinkFieldIdFromElementId(gridId)
  }

  // ref:… — словарная цепочка стартует от значения первого hop-поля корневого ряда
  // (резолв через passport-цепочку, на который он фолбэчится, читает то же поле).
  if (gridId.startsWith('ref:')) {
    const refSegments = parseRefHopChainFromGridId(gridId)
    if (refSegments.length > 0) return refSegments[0].field_id
  }

  // passport-цепочка: с хопами — первый edge; без хопов — само leaf-поле на корневом ряду.
  const segments = parseHopSegmentsForLinkedResolution(item, catalogTypeId, catalogFields, fieldResolved)
  if (segments.length > 0) return segments[0].field_id
  return parseLeafFieldIdForLinkedResolution(item, fieldResolved)
}

/**
 * Собирает id полей-источников, значения которых реально влияют на резолв
 * связанных/ссылочных ячеек (`buildLinkedPassportDisplayMapForRuntime`).
 *
 * Нужен для сужения триггера: пересчитывать display-map стоит только при правке
 * этих полей, а не любого значения паспорта (иначе запрос летит на каждый ввод).
 */
export function collectLinkedSourceFieldIds(
  layoutTabs: CollectParentGridItemsInput['layoutTabs'],
  catalogTypeId: number,
  fieldMetadata: ObjectInventoryField[],
): string[] {
  const metadataById = new Map(fieldMetadata.map((f) => [String(f.id), f]))
  const items = collectParentGridFieldItemsForLinkedResolution({
    layoutTabs,
    catalogTypeId,
    metadataById,
  })
  if (!items.length) return []

  const catalogFields = fieldMetadata.length > 0 ? fieldMetadata : []
  const out = new Set<string>()
  for (const { layoutItem, field } of items) {
    const resolved =
      field ??
      metadataById.get(
        String((layoutItem as GridFieldType & { field_id?: string | number }).field_id ?? ''),
      ) ??
      metadataById.get(String(parseLeafFieldIdForLinkedResolution(layoutItem, undefined) ?? ''))
    const sourceId = resolveLinkedSourceFieldId(layoutItem, catalogTypeId, catalogFields, resolved)
    if (sourceId != null && sourceId !== '') out.add(String(sourceId))
  }
  return [...out]
}

/**
 * Loads linked passport rows along `list_types` paths and returns display strings keyed by **grid cell id** (`layoutItem.id`).
 */
export async function buildLinkedPassportDisplayMapForRuntime(
  http: HttpGet,
  filter: RuntimeFilter,
  rootFieldValues: RuntimeFieldValueMap,
  layoutTabs: CollectParentGridItemsInput['layoutTabs'],
  catalogTypeId: number,
  fieldMetadata: ObjectInventoryField[],
  /** Route `objectId` — group id for `/inventory/group/{id}/…` (not hardcoded). */
  groupId: string,
  /** Shown for synthetic `parent-obj-name:*` cells (from `element.parent_object.name`). */
  parentObjectDisplayName?: string,
): Promise<Record<string, string>> {
  const metadataById = new Map(fieldMetadata.map((f) => [String(f.id), f]))
  const items = collectParentGridFieldItemsForLinkedResolution({
    layoutTabs,
    catalogTypeId,
    metadataById,
  })
  if (!items.length) return {}

  const catalogFields =
    fieldMetadata.length > 0 ? fieldMetadata : []

  const entries = await Promise.all(
    items.map(async ({ layoutItem, field }) => {
      const gridId = String(layoutItem.id)
      const resolved =
        field ??
        metadataById.get(
          String((layoutItem as GridFieldType & { field_id?: string | number }).field_id ?? ''),
        ) ??
        metadataById.get(String(parseLeafFieldIdForLinkedResolution(layoutItem, undefined) ?? ''))
      const value = await resolveDisplayForGridItem(
        http,
        filter,
        groupId,
        rootFieldValues,
        layoutItem,
        catalogTypeId,
        catalogFields,
        resolved,
        dictCacheSingleton,
        dictInflightSingleton,
        parentObjectDisplayName,
      )
      return [gridId, value] as const
    }),
  )

  return Object.fromEntries(entries)
}
